import React, { useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import {
  formatAdminDisplayDate,
  getAdminMaxSelectableDate,
  getLocalISODate,
} from "../utils";
import { DesktopIcon, MonitorIcon } from "./Shared";
import { useSelector } from "react-redux";
import {
  canEditModule,
  getModulePermissions,
  isBookingAdmin,
} from "../../../../../utils/moduleAccess";

const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const normalizeValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isBlockedBooking = (booking = {}) =>
  normalizeValue(
    booking.booking_details?.restriction_type || booking.restrictionType,
  ) === "block" ||
  normalizeValue(booking.source) === "seatblock" ||
  (normalizeValue(booking.userName || booking.bookedFor) === "n/a" &&
    !booking.bookedFor);

const getQuickDates = () => {
  const dates = [];
  let i = 0;
  let added = 0;
  while (added < 7) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      dates.push(getLocalISODate(d));
      added += 1;
    }
    i += 1;
  }
  return dates;
};

const CalendarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const SearchIcon = () => (
  <svg
    className="h-4 w-4 text-slate-400"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const ActivityIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
  </svg>
);

const FILTER_TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "permanent", label: "Permanent" },
  { value: "recurring", label: "Recurring" },
  { value: "one-time", label: "One-Time" },
  { value: "date-range", label: "Date-Range" },
];

const FILTER_DEVICE_OPTIONS = [
  { value: "all", label: "All Devices" },
  { value: "monitor", label: "Monitor" },
  { value: "desktop", label: "Desktop" },
  { value: "laptop", label: "Laptop" },
];

const bookingSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "6px",
    borderColor: state.isFocused ? "#e2e8f0" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(15, 23, 42, 0.06)" : "none",
    paddingLeft: "4px",
    paddingRight: "4px",
    fontSize: "0.875rem",
    fontWeight: 600,
    "&:hover": { borderColor: "#cbd5e1" },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#0f172a",
    fontWeight: 600,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontWeight: 600,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#64748b",
    padding: "8px",
    "&:hover": {
      color: "#0f172a",
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#64748b",
    padding: "8px",
    "&:hover": {
      color: "#0f172a",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 30,
    borderRadius: "16px",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.875rem",
    fontWeight: 600,
    backgroundColor: state.isSelected
      ? "#0f172a"
      : state.isFocused
        ? "#f8fafc"
        : "white",
    color: state.isSelected ? "white" : "#0f172a",
    "&:active": {
      backgroundColor: state.isSelected ? "#0f172a" : "#e2e8f0",
    },
  }),
};

const GlobalBookingsList = ({
  allBookingsByDate,
  filterDate,
  setFilterDate,
  filterType,
  setFilterType,
  filterSeatCapability,
  setFilterSeatCapability,
  searchTerm,
  setSearchTerm,
  currentPage,
  setCurrentPage,
  pagination,
  handleCancelBooking,
}) => {
  const authState = useSelector((state) => state.auth);
  const modulePermissions = getModulePermissions(authState);
  const isAuthorized = canEditModule(modulePermissions, "booking") && isBookingAdmin(authState);

  const dateInputRef = useRef(null);
  const quickDates = useMemo(() => getQuickDates(), []);
  const maxFilterDate = useMemo(
    () => getAdminMaxSelectableDate(new Date()),
    [],
  );
  const totalPages = pagination?.totalPages || null;
  const hasNext = pagination?.hasNext || false;
  const total = pagination?.total;
  const visibleBookings = allBookingsByDate;
  const selectedFilterType =
    FILTER_TYPE_OPTIONS.find((option) => option.value === filterType) || null;
  const selectedDeviceType =
    FILTER_DEVICE_OPTIONS.find((option) => option.value === filterSeatCapability) || null;

  const handleCustomClick = () => {
    if (!dateInputRef.current) return;
    if (dateInputRef.current.showPicker) {
      dateInputRef.current.showPicker();
      return;
    }
    dateInputRef.current.click();
  };

  return (
    <div className="mb-6 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_48px_rgba(15,23,42,0.08)]">
      <div className="border-b border-slate-100 bg-[linear-gradient(180deg,#ffffff_0%,#fbfbfb_100%)] px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">
              Admin View
            </p>
            <h2 className="mt-1 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
              All bookings by date
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Permanent assignments appear on every date
            </p>
          </div>
        </div>
      </div>

      <div className="border-b border-slate-100 px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {quickDates.map((date) => {
              const d = new Date(`${date}T00:00:00`);
              const isToday = date === getLocalISODate(new Date());
              const isSelected = date === filterDate;

              return (
                <button
                  key={date}
                  type="button"
                  onClick={() => setFilterDate(date)}
                  className={`flex min-w-[74px] flex-col items-center rounded-[18px] border px-4 py-3 transition-all ${
                    isSelected
                      ? "border-slate-900 bg-slate-900 text-white shadow-[0_14px_32px_rgba(15,23,42,0.18)]"
                      : "border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <span className="text-[10px] font-black uppercase tracking-[0.18em] opacity-70">
                    {isToday ? "Today" : DAY_ABBR[d.getDay()]}
                  </span>
                  <span className="text-lg font-black leading-none">
                    {d.getDate()}
                  </span>
                </button>
              );
            })}

            <div className="relative admin-custom-date-picker">
              <DatePicker
                selected={
                  !quickDates.includes(filterDate)
                    ? new Date(`${filterDate}T00:00:00`)
                    : null
                }
                onChange={(date) => {
                  if (date) setFilterDate(getLocalISODate(date));
                }}
                minDate={new Date()}
                maxDate={maxFilterDate}
                filterDate={(date) =>
                  date.getDay() !== 0 && date.getDay() !== 6
                }
                dateFormat="dd-MM-yyyy"
                portalId="root"
                customInput={
                  <button
                    type="button"
                    className={`flex min-w-[82px] flex-col items-center justify-center rounded-[18px] border px-4 py-3 transition-all ${
                      !quickDates.includes(filterDate)
                        ? "border-slate-900 bg-slate-900 text-white shadow-[0_14px_32px_rgba(15,23,42,0.18)]"
                        : "border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <span className="text-[10px] font-black uppercase tracking-[0.18em] opacity-70">
                      Custom
                    </span>
                    <span className="mt-1 text-base font-black leading-none">
                      {!quickDates.includes(filterDate) ? (
                        new Date(`${filterDate}T00:00:00`).getDate()
                      ) : (
                        <CalendarIcon />
                      )}
                    </span>
                  </button>
                }
              />
            </div>
          </div>
          {isAuthorized && (
            <div>
              <Link to="/bookings/activity-log" className="flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-slate-800 shadow-[0_4px_12px_rgba(15,23,42,0.12)]">
                <ActivityIcon /> Activity Log
              </Link>
            </div>
          )}
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
            {/* Search */}
            <div className="w-full lg:flex-1 lg:min-w-0 lg:max-w-[700px]">
              <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-3 shadow-sm transition-all">
                <SearchIcon />

                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search person, seat, or section..."
                  className="w-full bg-transparent text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400"
                />

                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="rounded-md bg-slate-100 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 transition hover:bg-slate-200"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Booking Count */}
              <p className="mt-2 pl-1 text-xs font-medium text-slate-500">
                <span className="font-semibold text-slate-700">
                  {total ?? visibleBookings.length}
                </span>{" "}
                bookings found
              </p>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center lg:gap-2">
              <div className="w-full lg:w-[175px] xl:w-[195px] 2xl:w-[220px]">
                <Select
                  value={selectedFilterType}
                  onChange={(option) => setFilterType(option?.value || "all")}
                  options={FILTER_TYPE_OPTIONS}
                  isClearable={selectedFilterType?.value !== "all"}
                  isSearchable={false}
                  placeholder="All Types"
                  styles={bookingSelectStyles}
                  classNamePrefix="dropdownSelect"
                />
              </div>

              <div className="w-full lg:w-[175px] xl:w-[195px] 2xl:w-[220px]">
                <Select
                  value={selectedDeviceType}
                  onChange={(option) => setFilterSeatCapability(option?.value || "all")}
                  options={FILTER_DEVICE_OPTIONS}
                  isClearable={selectedDeviceType?.value !== "all"}
                  isSearchable={false}
                  placeholder="All Devices"
                  styles={bookingSelectStyles}
                  classNamePrefix="dropdownSelect"
                />
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between gap-2 sm:justify-end lg:ml-auto">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="h-12 rounded-md border border-slate-200 bg-white px-5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>

              <span className="whitespace-nowrap text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">
                Page {currentPage}
                {totalPages ? ` / ${totalPages}` : ""}
              </span>

              <button
                type="button"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!hasNext}
                className="h-12 rounded-md border border-slate-200 bg-white px-5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600 transition-all hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 py-5 sm:px-6">
        {visibleBookings.length === 0 ? (
          <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-400">
            No bookings for{" "}
            <span className="font-bold text-slate-700">
              {formatAdminDisplayDate(filterDate)}
            </span>
          </div>
        ) : (
          <div className="overflow-auto rounded-2xl border border-slate-100">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                    Person
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                    Seat
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                    Section
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                    Workstation
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-slate-50">
                    {(() => {
                      const blocked = isBlockedBooking(booking);
                      const bookingType = booking.bookingType || "one-time";
                      return (
                        <>
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {blocked ? "Blocked" : booking.bookedFor}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {booking.seatLabel}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {booking.sectionName}
                          </td>
                          <td className="px-4 py-3">
                            {bookingType === "permanent" ? (
                              <span className="inline-flex rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-violet-700">
                                Permanent
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium capitalize text-slate-600">
                                {bookingType}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize tracking-wide ${
                                (booking.seatType || "laptop").toLowerCase() ===
                                "monitor"
                                  ? "bg-blue-50 text-blue-700 border border-blue-100"
                                  : (
                                        booking.seatType || "laptop"
                                      ).toLowerCase() === "desktop"
                                    ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                    : "bg-amber-50 text-amber-700 border border-amber-100"
                              }`}
                            >
                              {(booking.seatType || "laptop").toLowerCase() ===
                              "monitor" ? (
                                <MonitorIcon className="w-3.5 h-3.5" />
                              ) : (
                                  booking.seatType || "laptop"
                                ).toLowerCase() === "desktop" ? (
                                <DesktopIcon className="w-3.5 h-3.5" />
                              ) : (
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                >
                                  <rect
                                    x="2"
                                    y="4"
                                    width="20"
                                    height="14"
                                    rx="2"
                                  />
                                  <path d="M2 18h20" />
                                </svg>
                              )}
                              {(booking.seatType || "laptop").toLowerCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() =>
                                handleCancelBooking(booking.id, {
                                  bookingType,
                                  isSeatBlock: blocked,
                                  forceStandard: !blocked,
                                  useIndividualBlockCancel: blocked,
                                })
                              }
                              className="rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
                            >
                              Cancel
                            </button>
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalBookingsList;
